/**
 * profitAndLoss.ts — Pure computation of a period's Profit & Loss: the first
 * question every shop owner asks ("did I make money this month") that the app
 * has never been able to answer.
 *
 * The figures and the rules they enforce:
 *
 *  1. **Revenue is ex-GST and net of returns.** GST collected is not income —
 *     it is held on behalf of the government. So turnover is
 *     `invoice.totalAmount − invoice.totalTax`, and a credit note (sign −1)
 *     subtracts both its value and its tax. A shop that sold ₹1,18,000
 *     (₹1,00,000 + 18% GST) and refunded ₹11,800 reports ₹90,000 of revenue.
 *  2. **COGS comes from the cost snapshot on the line, never the product's
 *     current purchase price.** `line_items.cost_price` is the unit cost frozen
 *     at sale time; the live purchase price mutates on every restock and would
 *     silently rewrite last month's margin. A returned line carries the
 *     original cost and sign −1, so a full return nets its COGS back to zero.
 *  3. **Missing cost is reported, not invented.** Lines booked before the cost
 *     snapshot existed have `costPrice === null`. They contribute revenue but no
 *     COGS, which overstates gross profit — so the result carries the share of
 *     lines (and of revenue) with no cost basis, for the screen to disclose,
 *     rather than pretending those sales were 100% margin. `0` is a real cost
 *     (a free sample), distinct from `null`.
 *
 * Pure — no DB access — so every netting and coverage rule is unit-testable.
 * See `pnlDataSource.ts` for the WatermelonDB bridge.
 */

export const CREDIT_NOTE = 'CREDIT_NOTE';

/** +1 for a sale, −1 for a credit note. Mirrors salesSummary.signOf. */
export function signOf(invoiceType: string): 1 | -1 {
  return invoiceType === CREDIT_NOTE ? -1 : 1;
}

/** Round to 2 decimals, killing binary-float dust (e.g. 0.1 + 0.2). */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface PnlInvoiceInput {
  type: string;
  /** Invoice total, tax-inclusive. */
  totalAmount: number;
  totalTax: number;
}

export interface PnlLineInput {
  quantity: number;
  /** Line total, tax-inclusive — used only to weight the missing-cost share. */
  totalAmount: number;
  /** Unit cost snapshot at sale time; `null` = no cost basis, `0` = real zero. */
  costPrice: number | null;
  /** Type of the invoice this line belongs to — drives the sign. */
  invoiceType: string;
}

export interface PnlExpenseInput {
  amount: number;
  categoryName: string;
}

export interface ProfitLossInput {
  /** SALE + CREDIT_NOTE for the range. Voided invoices must already be excluded. */
  invoices: PnlInvoiceInput[];
  lines: PnlLineInput[];
  /** Expenses dated within the range (by `expense_date`). */
  expenses: PnlExpenseInput[];
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface ExpenseCategoryTotal {
  name: string;
  amount: number;
}

export interface ProfitLoss {
  /** Sale turnover before returns, ex-GST. */
  grossRevenue: number;
  /** Credit-note turnover in the range, ex-GST. */
  returns: number;
  /** grossRevenue − returns. The number that means "revenue". */
  netRevenue: number;
  /** Cost of goods sold, net of returned units; only lines with a cost basis. */
  cogs: number;
  /** netRevenue − cogs. */
  grossProfit: number;
  /** grossProfit / netRevenue, as a fraction (0 when netRevenue ≤ 0). */
  grossMargin: number;
  /** Sum of expenses in the range. */
  totalExpenses: number;
  /** Expenses grouped by category, largest first. */
  expensesByCategory: ExpenseCategoryTotal[];
  /** grossProfit − totalExpenses. */
  netProfit: number;
  /** netProfit / netRevenue, as a fraction (0 when netRevenue ≤ 0). */
  netMargin: number;

  saleCount: number;
  creditNoteCount: number;

  // ── Cost-basis coverage (data quality) ──
  /** Distinct sold lines considered (sale + credit-note lines). */
  lineCount: number;
  /** Lines whose costPrice is null — no cost basis. */
  linesWithoutCost: number;
  /** linesWithoutCost / lineCount, as a fraction (0 when no lines). */
  linesWithoutCostShare: number;
  /**
   * Share of (gross, tax-inclusive) sale line value whose lines have no cost
   * basis — a revenue-weighted honesty signal, since one big un-costed line
   * distorts margin more than many tiny ones. Fraction; 0 when no sale value.
   */
  revenueWithoutCostShare: number;
}

const safeDivide = (num: number, den: number): number => (den > 0 ? num / den : 0);

export function buildProfitAndLoss(input: ProfitLossInput): ProfitLoss {
  const { invoices, lines, expenses } = input;

  const sales = invoices.filter((i) => i.type !== CREDIT_NOTE);
  const credits = invoices.filter((i) => i.type === CREDIT_NOTE);

  // Revenue ex-GST: strip the tax the invoice collected on the state's behalf.
  const exTax = (i: PnlInvoiceInput) => i.totalAmount - i.totalTax;
  const grossRevenue = sales.reduce((s, i) => s + exTax(i), 0);
  const returns = credits.reduce((s, i) => s + exTax(i), 0);
  const netRevenue = grossRevenue - returns;

  // COGS, net of returned units. Only lines that actually carry a cost.
  const cogs = lines.reduce((s, li) => {
    if (li.costPrice == null) return s;
    return s + signOf(li.invoiceType) * li.quantity * li.costPrice;
  }, 0);

  const grossProfit = netRevenue - cogs;

  // Expenses, grouped by category name.
  const byCategory = new Map<string, number>();
  for (const e of expenses) {
    byCategory.set(e.categoryName, (byCategory.get(e.categoryName) ?? 0) + e.amount);
  }
  const expensesByCategory = [...byCategory.entries()]
    .map(([name, amount]) => ({ name, amount: round2(amount) }))
    .sort((a, b) => b.amount - a.amount);
  const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);

  const netProfit = grossProfit - totalExpenses;

  // ── Cost-basis coverage ──
  const lineCount = lines.length;
  const linesWithoutCost = lines.filter((li) => li.costPrice == null).length;

  // Revenue-weighted: measure the un-costed share against *sale* line value,
  // so returns don't distort the denominator toward zero.
  const saleLines = lines.filter((li) => li.invoiceType !== CREDIT_NOTE);
  const saleLineValue = saleLines.reduce((s, li) => s + li.totalAmount, 0);
  const saleLineValueNoCost = saleLines
    .filter((li) => li.costPrice == null)
    .reduce((s, li) => s + li.totalAmount, 0);

  return {
    grossRevenue: round2(grossRevenue),
    returns: round2(returns),
    netRevenue: round2(netRevenue),
    cogs: round2(cogs),
    grossProfit: round2(grossProfit),
    grossMargin: safeDivide(grossProfit, netRevenue),
    totalExpenses: round2(totalExpenses),
    expensesByCategory,
    netProfit: round2(netProfit),
    netMargin: safeDivide(netProfit, netRevenue),
    saleCount: sales.length,
    creditNoteCount: credits.length,
    lineCount,
    linesWithoutCost,
    linesWithoutCostShare: safeDivide(linesWithoutCost, lineCount),
    revenueWithoutCostShare: safeDivide(saleLineValueNoCost, saleLineValue),
  };
}
